// AI Generated Component
import { Component, OnInit, OnDestroy, Input, NgZone, inject, signal } from '@angular/core';
import { Observable, Subscription, interval } from 'rxjs';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { CrawlsRepoService } from '@client/app/services/api/crawls-repo/crawls-repo.service';
import { DateTimeService } from '@client/app/services/date-time/date-time.service';
import { DomainRepoService } from '@client/app/services/api/domain-repo/domain-repo.service';
import { ToastService } from '@monsido/angular-shared-components';
import { MonDialog } from '@client/app/modules/dialog-upgrade-wrapper/mon-dialog.service';
import { CoreDomainService } from '@client/app/core/services/domain/core-domain.service';
import { CRAWL_STATE } from '@client/ng2/core/constants/crawlState.constant';

@Component({
    selector: 'scan-details-dialog',
    standalone: false,
    template: `
        <div class="mon-dialog-header">
            <div class="pt-1">
                <page-header [header]="'Crawl status' | translate"></page-header>
            </div>
        </div>
        <div class="mon-dialog-body bg-white scan-details-dialog-body">
            <div class="px-6 mt-2">
                <h3 class="text-center my-6" translate *ngIf="isDone">
                    Scan is done
                </h3>
                <div *ngIf="!isDone">
                    <h3 data-test="ongoing-scan-domain-url"> {{domain?.url}}</h3>
                    <h4> {{domain?.title}}</h4>
                    <div class="row mt-3">
                        <div class="col-sm-12 text-left text-black font-weight-500 pr-0">
                            <span data-test="scan-in-progress-text" translate>Scan in progress:</span>
                        </div>
                        <div class="col-sm-36">
                            <ngb-progressbar class="progress-striped active" [max]="100" [value]="domain?.crawl_status?.progress"></ngb-progressbar>
                        </div>
                    </div>
                    <div class="row" *ngIf="crawl?.state !== POSTPROCESSING && crawl?.state !== COOKIE_SCAN">
                        <div class="col-sm-12 text-left text-black font-weight-500 pr-0">
                            <span translate>Scanning URL:</span>
                        </div>
                        <div class="col-sm-36">
                            {{crawl?.details}}
                        </div>
                    </div>
                    <div class="row mt-2">
                        <div class="col-sm-12 text-left text-black font-weight-500 pr-0">
                            <span translate>Crawl status:</span>
                        </div>
                        <div data-test="crawl-state" class="col-sm-36">
                            {{crawl?.state}}
                        </div>
                    </div>
                    <div class="row mt-1">
                        <div class="col-sm-12 text-left text-black font-weight-500 pr-0">
                            <span translate>Crawl started:</span>
                        </div>
                        <div class="col-sm-36">
                            <user-date [twoLines]="true" [date]="crawl?.queued_at"></user-date>
                        </div>
                    </div>
                    <div class="row mt-1">
                        <div class="col-sm-12 text-left text-black font-weight-500 pr-0">
                            <span translate>Crawl runtime:</span>
                        </div>
                        <div class="col-sm-36" *ngIf="crawl">
                            {{timeDiff()}}
                        </div>
                    </div>
                    <div class="row mt-1" *ngIf="crawl?.state !== POSTPROCESSING && crawl?.state !== COOKIE_SCAN">
                        <div class="col-sm-12 text-left text-black font-weight-500 pr-0">
                            <span translate>Scanned pages:</span>
                        </div>
                        <div class="col-sm-36">
                            {{(crawl?.page_count || 0) | monNumber}}
                        </div>
                    </div>
                    <div class="row mt-1" *ngIf="crawl?.state !== POSTPROCESSING && crawl?.state !== COOKIE_SCAN">
                        <div class="col-sm-12 text-left text-black font-weight-500 pr-0">
                            <span translate>Scanned links:</span>
                        </div>
                        <div class="col-sm-36">
                            {{(crawl?.scanned_count || 0) | monNumber}}
                        </div>
                    </div>
                </div>
                <div *ngIf="crawl && crawl.state === RETRY" class="stop-scan-block">
                    <p class="text-center mt-5">
                        <span translate>Your scan is stalled with a Retry status. This usually means we're having trouble reaching your domain,
                            possibly due to your infrastructure blocking our requests. </span>
                        <span translate>Please stop the scan,
                            <a href="https://docs.acquia.com/acquia-optimize/scan-troubleshooting-checklist" target="_blank">
                                check that your website is configured to allow scanning</a>, and then start a new scan.</span>
                    </p>
                    <div class="text-center my-2">
                        <button class="btn emphasised-btn"
                                monEventTrigger="actionTrigger"
                                [monEventParams]="{action: 'crawl-status-stop-scan-btn'}"
                                (click)="stopScan()"
                                [disabled]="stopping"
                                data-test="stop-scan-button"
                        >
                            <span [ngClass]="{'fas fa-spinner fa-spin fa-fw ': stopping}" aria-hidden="true"></span>
                            <span>{{ 'Stop scan' | translate }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `,
})
export class ScanDetailsDialogComponent implements OnInit, OnDestroy {
    @Input() domainObservable?: Observable<any>;
    @Input() monDialogId?: string;

    isDone = false;
    domain: any;
    crawl: any;
    stopping = false;
    POSTPROCESSING = CRAWL_STATE.POSTPROCESSING;
    COOKIE_SCAN = CRAWL_STATE.COOKIE_SCAN;
    RETRY = CRAWL_STATE.RETRY;

    ngZone = inject(NgZone);
    readonly timeDiff = signal('');
    timeDiffCalculationInterval: ReturnType<typeof setInterval> | undefined;

    private domainSubscription?: Subscription | undefined;
    private timerPromise?: Subscription | undefined;

    constructor (
        private crawlsRepoService: CrawlsRepoService,
        private dateTimeService: DateTimeService,
        private domainRepoService: DomainRepoService,
        private toastService: ToastService,
        private translateService: TranslateService,
        private monDialog: MonDialog,
        private coreDomainService: CoreDomainService,
    ) {}

    ngOnInit (): void {
        this.subscribeDomain();
        this.getScan().finally(() => {
            this.startAutoReload();
        });

        this.ngZone.runOutsideAngular(() => {
            this.timeDiffCalculationInterval = setInterval(() => {
                const timeDiff = this.calcTimeDiff();
                if (this.timeDiff() !== timeDiff) {
                    this.ngZone.run(() => {
                        this.timeDiff.set(timeDiff);
                    });
                }
            }, 1000);
        });
    }

    ngOnDestroy (): void {
        this.destroyTimer();
        if (this.timeDiffCalculationInterval) {
            clearInterval(this.timeDiffCalculationInterval);
        }
    }

    private subscribeDomain (): void {
        if (this.domainObservable) {
            this.domainSubscription = this.domainObservable.subscribe((domain: any) => {
                this.domain = domain;
                this.isDone = !domain || !domain.crawl_status;
            });
        }
    }

    private getScan (): Promise<any> {
        return this.crawlsRepoService.getScanDetails().then(
            (data: any) => {
                this.crawl = data;
            },
            (response: any) => {
                if (parseInt(response.status) === 404) {
                    this.isDone = true;
                    this.destroyTimer();
                }
            },
        );
    }

    private startAutoReload (): void {
        this.timerPromise = interval(13000).subscribe(() => {
            this.getScan();
        });
    }

    private destroyTimer (): void {
        if (this.timerPromise) {
            this.timerPromise.unsubscribe();
            this.timerPromise = undefined;
        }
        if (this.domainSubscription) {
            this.domainSubscription.unsubscribe();
            this.domainSubscription = undefined;
        }
    }

    calcTimeDiff (): string {
        if (!this.crawl?.queued_at) {
            return '';
        }
        return this.dateTimeService.timeDiffString(undefined, this.crawl.queued_at);
    }

    stopScan (): void {
        this.stopping = true;
        this.domainRepoService.stopCrawl(this.domain.id)
            .then(() => {
                this.toastService.success(this.translateService.getString('Scan stopped successfully'));
                this.coreDomainService.refreshDomain();
                this.monDialog.closeAll(() => {}, false);
                this.stopping = false;
            }).catch(() => {
                this.stopping = false;
            });
    }
}
